// ---------------------------------------------------------------------------
// The registry of the 8 real models (6 rainfall-runoff + 2 snow) — wiring
// each `calculateXxx()`'s own named parameters/state to the generic
// `Map<string, number>` shape `engine.ts` resolves from the project's
// catalogs, plus the small "which code in THIS project is this model"
// lookup. Split out of engine.ts (see project_evalhid_code_audit.md's
// ongoing "god module" breakup) — this block is purely declarative wiring,
// with no dependency on engine.ts's own dispatch/aggregation logic, unlike
// everything left there.
// ---------------------------------------------------------------------------
import { TranslatableError } from "../i18n/translate.ts";
import type { EvalHidProject } from "../model/types.ts";
import { calculateAwbm } from "./awbm.ts";
import { calculateGr2m } from "./gr2m.ts";
import { calculateGr4j } from "./gr4j.ts";
import { calculateHbv } from "./hbv.ts";
import { calculateSnow1 } from "./snow1.ts";
import { calculateSnow2 } from "./snow2.ts";
import { calculateSac } from "./sac.ts";
import { calculateTemez } from "./temez.ts";

/** The 6 rainfall-runoff models with a real engine (REAL_MODELS below) — a shared literal type, rather than plain `string`, so a typo in aggregation.ts's SUP_SUBT_KEYS (or anywhere else matching against `SubBasinResult.model`) is a compile error instead of a silently-never-matching key. */
export type RealModelName = "Témez" | "HBV" | "GR2M" | "GR4J" | "AWBM" | "SAC";

/**
 * Bound getter for a model's `params`/`state` map that THROWS instead of
 * silently continuing with `undefined` — replaces the plain
 * `params.get("X")!` non-null assertion every model used to build its
 * (required, non-optional) Parameters/State object with. A real gap here
 * (a catalog missing a default, or a corrupted/partially-imported v1.0
 * `.ehd`) used to flow through as `NaN` on the first arithmetic step and
 * silently corrupt the entire simulation, with `calculationError`'s own
 * `.catch()` never firing since nothing ever threw (found in the
 * 2026-08-24 structure/maintainability review). Kept generic over `kind`
 * purely so the error names the right catalog — `params`/`state` share
 * the exact same `Map<string, number>` shape.
 */
function requiredExtractor(
  values: Map<string, number>,
  modelTypeName: string,
  kind: "parameter" | "state variable"
): (key: string) => number {
  return (key: string) => {
    const value = values.get(key);
    if (value === undefined) {
      throw new TranslatableError(
        kind === "parameter" ? "engine.missingParameter" : "engine.missingStateVariable",
        { key, model: modelTypeName }
      );
    }
    return value;
  };
}

/**
 * A rainfall-runoff model with a real engine: name exactly as it appears in
 * APO_TIPO_MODELO (to resolve its code in each project, see
 * `realModelCodes`) and the function that, given its parameters/state
 * variables already extracted by name, calculates a sub-basin's
 * contribution.
 */
interface RealModel {
  modelTypeName: RealModelName;
  calculate: (
    precip: Float64Array,
    etp: Float64Array,
    params: Map<string, number>,
    state: Map<string, number>,
    /** Whether the sub-basin has a real aquifer assigned — all 6 models exclude their own underground/base-flow component from `contribution` when true (see each model's own header). */
    hasAquifer: boolean,
    /**
     * The sub-basin's own pumping (bomb) series, by its own resolved
     * `SubBasin.pumpingCol` — `null` when it has no column of its own (never
     * inherited from its aquifer's, see `SubBasin.pumpingCol`'s own doc
     * comment) or when pumping isn't being modeled this run at all (`Op_
     * bombeos`/"Modelar bombeos" unchecked — see calculationOptions.ts,
     * useCalculation.ts's `seriesForCalculation`).
     */
    pumping: Float64Array | null
  ) => { contribution: Float64Array; detail: Record<string, Float64Array> };
}

/**
 * Adding a 7th rainfall-runoff model — every real touch point, verified by
 * grepping the 6 existing ones (not just guessed): no registry ties these
 * together, so it's easy to add a model that "works" (compiles, computes a
 * number) but is silently wrong or incomplete in one of the other spots.
 *
 * 1. `src/engine/<model>.ts` (new file) — `calculateXxx()`, same shape as
 *    the 6 existing ones (`{ contribution, detail }`).
 * 2. THIS FILE — add the name to `RealModelName` (forces every `Record<
 *    RealModelName, ...>` elsewhere to a compile error until updated — see
 *    its own comment), import `calculateXxx`, add an entry to `REAL_MODELS`
 *    below.
 * 3. `aggregation.ts`'s `SUP_SUBT_KEYS` — ⚠️ `Partial<Record<RealModelName,
 *    ...>>`, so TypeScript does NOT force this one. Forgetting it doesn't
 *    error — the model's contribution silently reports as
 *    `noBreakdownVolume` instead of `surfaceVolume`/`groundwaterVolume`
 *    (see that file's own comment on why some models are deliberately
 *    absent from this table — GR2M/SAC — vs. accidentally missing).
 * 4. `model/baseTemplate.ts` — `MODEL_TYPES` entry + its parameter/state-
 *    variable definitions (`PARAMETER_DEFINITIONS`/state var defs).
 * 5. `model/calibrationParameterRanges.ts` — one `RANGES` entry per
 *    parameter, keyed by `(modelTypeCode, kind, parameterCode)` from
 *    baseTemplate.ts — nothing here is type-checked against baseTemplate at
 *    all, so a forgotten entry only surfaces if/when someone tries to
 *    calibrate that model (`requiredExtractor`-style failure at runtime).
 * 6. `i18n/help.en.ts` / `help.es.ts` — the "rainfall-runoff models" help
 *    entry names the 6 models explicitly (`keywords` + body text) — purely
 *    informational, but goes stale otherwise.
 * 7. A dedicated `test/<model>.test.ts` with hand-derived expected values
 *    (established pattern for all 6 existing models, see CLAUDE.md).
 *
 * Explicitly NOT required (checked, not just assumed): `model/types.ts`,
 * `model/entityNames.ts`, `model/validations.ts`, `model/examples.ts`,
 * `model/exportCsvProject.ts`, `engine/exportCsv.ts`, `ui/CalculationResults.tsx`,
 * `ui/wizard/Step1SubBasins.tsx`, the main `i18n/es.ts`/`en.ts` — none key
 * anything by model name; they work generically off `modelTypeCode` /
 * `RealModelName` already.
 */
export const REAL_MODELS: RealModel[] = [
  {
    modelTypeName: "Témez",
    calculate: (precip, etp, params, state, hasAquifer, pumping) => {
      const p = requiredExtractor(params, "Témez", "parameter");
      const s = requiredExtractor(state, "Témez", "state variable");
      return calculateTemez(
        precip,
        etp,
        { hmax: p("Hmax"), c: p("C"), imax: p("Imax"), alfa: p("Alfa") },
        { ho: s("Ho"), vo: s("Vo") },
        hasAquifer,
        pumping
      );
    },
  },
  {
    modelTypeName: "HBV",
    calculate: (precip, etp, params, state, hasAquifer, pumping) => {
      const p = requiredExtractor(params, "HBV", "parameter");
      const s = requiredExtractor(state, "HBV", "state variable");
      return calculateHbv(
        precip,
        etp,
        {
          beta: p("Beta"),
          fc: p("FC"),
          pwp: p("Pwp"),
          lmax: p("Lmax"),
          k0: p("K0"),
          k1: p("K1"),
          k2: p("K2"),
          kperc: p("Kperc"),
        },
        { ho: s("Ho"), s1: s("S1"), s2: s("S2") },
        hasAquifer,
        pumping
      );
    },
  },
  {
    modelTypeName: "GR2M",
    calculate: (precip, etp, params, state, hasAquifer, pumping) => {
      const p = requiredExtractor(params, "GR2M", "parameter");
      const s = requiredExtractor(state, "GR2M", "state variable");
      return calculateGr2m(
        precip,
        etp,
        { x1: p("X1"), x2: p("X2") },
        { so: s("So"), ro: s("Ro") },
        hasAquifer,
        pumping
      );
    },
  },
  {
    modelTypeName: "GR4J",
    calculate: (precip, etp, params, state, hasAquifer, pumping) => {
      const p = requiredExtractor(params, "GR4J", "parameter");
      const s = requiredExtractor(state, "GR4J", "state variable");
      return calculateGr4j(
        precip,
        etp,
        { x1: p("X1"), x2: p("X2"), x3: p("X3"), x4: p("X4") },
        { so: s("So"), ro: s("Ro") },
        hasAquifer,
        pumping
      );
    },
  },
  {
    modelTypeName: "AWBM",
    calculate: (precip, etp, params, state, hasAquifer, pumping) => {
      const p = requiredExtractor(params, "AWBM", "parameter");
      const s = requiredExtractor(state, "AWBM", "state variable");
      return calculateAwbm(
        precip,
        etp,
        { cave: p("Cave"), bfi: p("Bfi"), ksurf: p("Ksurf"), kb: p("Kb") },
        { have: s("Have"), vsup: s("Vsup"), vacu: s("Vacu") },
        hasAquifer,
        pumping
      );
    },
  },
  {
    modelTypeName: "SAC",
    calculate: (precip, etp, params, state, hasAquifer, pumping) => {
      const p = requiredExtractor(params, "SAC", "parameter");
      const s = requiredExtractor(state, "SAC", "state variable");
      return calculateSac(
        precip,
        etp,
        {
          uztwm: p("Uztwm"),
          uzfwm: p("Uzfwm"),
          lztwm: p("Lztwm"),
          lzfpm: p("Lzfpm"),
          lzfsm: p("Lzfsm"),
          uzk: p("Uzk"),
          lzpk: p("Lzpk"),
          lzsk: p("Lzsk"),
          zperc: p("Zperc"),
          rexp: p("Rexp"),
          pfree: p("Pfree"),
          pctim: p("Pctim"),
          adimp: p("Adimp"),
          riva: p("Riva"),
          side: p("Side"),
          saved: p("Saved"),
        },
        {
          uztwc: s("uztwc"),
          uzfwc: s("uzfwc"),
          lztwc: s("lztwc"),
          lzfsc: s("lzfsc"),
          lzfpc: s("lzfpc"),
          adim: s("adim"),
        },
        hasAquifer,
        pumping
      );
    },
  },
];

/**
 * A snow model with a real engine: same pattern as RealModel, but
 * calculating liquid precipitation (the rainfall-runoff model's input)
 * instead of contribution.
 */
export interface RealSnowModel {
  modelTypeName: string;
  calculate: (
    precip: Float64Array,
    temp: Float64Array,
    params: Map<string, number>,
    state: Map<string, number>
  ) => { liquidPrecipitation: Float64Array; detail: Record<string, Float64Array> };
}

export const REAL_SNOW_MODELS: RealSnowModel[] = [
  {
    modelTypeName: "Nieve1",
    calculate: (precip, temp, params, state) => {
      const p = requiredExtractor(params, "Nieve1", "parameter");
      const s = requiredExtractor(state, "Nieve1", "state variable");
      return calculateSnow1(precip, temp, { tt: p("TT"), dd: p("DD") }, { no: s("No") });
    },
  },
  {
    modelTypeName: "Nieve2",
    calculate: (precip, temp, params, state) => {
      const p = requiredExtractor(params, "Nieve2", "parameter");
      const s = requiredExtractor(state, "Nieve2", "state variable");
      return calculateSnow2(
        precip,
        temp,
        {
          an: p("An"),
          bp: p("Bp"),
          ocr: p("Ocr"),
          tcp1: p("Tcp1"),
          tcp2: p("Tcp2"),
          tcr: p("Tcr"),
        },
        { ho: s("Ho"), oo: s("Oo") }
      );
    },
  },
];

/**
 * Keys of a model's own `detail` (see engine.ts's `SubBasinResult`) that
 * make up its surface/groundwater runoff — confirmed against the real
 * source (`Aportaciones.cs`'s `acumula_apo`/each model's own `.cs`), per
 * model:
 *   - Témez: esc_sup = ti - infilt, esc_subt = vo - vi + infilt
 *   - HBV:   esc_sup = q0 + q1,     esc_subt = q2
 *   - GR4J:  esc_sup = direct runoff, esc_subt = base runoff — ALWAYS
 *     calculated, whether or not the sub-basin has a real aquifer assigned
 *     (see gr4j.ts's header note 2); only `contribution` itself drops the
 *     base component with an aquifer, `detail.baseRunoff` never does
 *   - AWBM:  same pattern, with the names this port already used
 *   - SAC:   esc_sup/esc_subt (Sac_sma.cs's own two-branch formula, see
 *     sac.ts's own comment on `surfaceRunoff`/`baseFlow`)
 *   - GR2M:  no surface component of its own — the whole flow counts as
 *     "underground" (`sup` intentionally empty, `sumKeys` treats a missing
 *     key as 0)
 *
 * Lives here (not in aggregation.ts, where it used to) so `engine.ts` can
 * precompute `SubBasinResult.aggregationDetail` from it directly, without a
 * circular import (aggregation.ts already imports FROM engine.ts) — see
 * `computeAggregationDetail` below and its own callers' comments for why
 * that precomputation exists at all (retaining `detail` in full for every
 * sub-basin of a huge project, just for this 2-3-key sum, was the single
 * largest source of memory use after a calculation).
 */
const SUP_SUBT_KEYS: Partial<Record<RealModelName, { sup: string[]; subt: string[] }>> = {
  Témez: { sup: ["surfaceRunoff"], subt: ["undergroundRunoff"] },
  HBV: { sup: ["q0", "q1"], subt: ["q2"] },
  GR4J: { sup: ["directRunoff"], subt: ["baseRunoff"] },
  AWBM: { sup: ["surfaceRunoff"], subt: ["baseFlow"] },
  SAC: { sup: ["surfaceRunoff"], subt: ["baseFlow"] },
  GR2M: { sup: [], subt: ["flow"] },
};

function sumKeys(detail: Record<string, Float64Array>, keys: string[], t: number): number {
  let total = 0;
  for (const key of keys) total += detail[key]?.[t] ?? 0;
  return total;
}

/**
 * Reduces a sub-basin's full step-by-step `detail` down to just the 2
 * arrays `aggregation.ts`'s `aggregateByKey` actually needs (surface/
 * groundwater runoff, per `SUP_SUBT_KEYS` above) — `undefined` for a
 * model without a real engine (the generic approximation) or one not
 * listed in `SUP_SUBT_KEYS` (SAC/GR2M's own case, `sup`/`subt` fixed at
 * `[]`/one key, not actually undefined — see that table's own comment),
 * matching `aggregateByKey`'s existing "no breakdown available" fallback.
 */
export function computeAggregationDetail(
  model: RealModelName | undefined,
  detail: Record<string, Float64Array> | undefined,
  stepCount: number
): { surface: Float64Array; groundwater: Float64Array } | undefined {
  if (!model || !detail) return undefined;
  const keys = SUP_SUBT_KEYS[model];
  if (!keys) return undefined;
  const surface = new Float64Array(stepCount);
  const groundwater = new Float64Array(stepCount);
  for (let t = 0; t < stepCount; t++) {
    surface[t] = sumKeys(detail, keys.sup, t);
    groundwater[t] = sumKeys(detail, keys.subt, t);
  }
  return { surface, groundwater };
}

/** APO_TIPO_MODELO code of each element of `models`, resolved by name so it doesn't depend on the codes always being the same. */
function modelCodes(
  project: EvalHidProject,
  models: { modelTypeName: string }[],
  kind: number
): Map<string, number> {
  const codes = new Map<string, number>();
  for (const model of models) {
    const code = project.modelTypes.find(
      (t) => t.kind === kind && t.modelTypeName === model.modelTypeName
    )?.modelTypeCode;
    if (code != null) codes.set(model.modelTypeName, code);
  }
  return codes;
}

export function realModelCodes(project: EvalHidProject): Map<string, number> {
  return modelCodes(project, REAL_MODELS, 0);
}

export function realSnowModelCodes(project: EvalHidProject): Map<string, number> {
  return modelCodes(project, REAL_SNOW_MODELS, 1);
}
